import { useState } from 'react';
import { Button } from '../../../../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../ui/popover';
import { Tags, FolderPlus, FolderMinus, X } from 'lucide-react';
import { epicengageSupabase } from '../../../../../integrations/supabase/client';
import { useMerchant } from '../../../../../hooks/useMerchant';
import { toast } from 'sonner';
import type { CustomerStatus } from '@/types/epicengage';

interface FilterContext {
  search: string;
  statusFilter: CustomerStatus | 'ALL' | 'UNSUBSCRIBED';
  selectedSegmentId: string;
  selectedTagIds: string[];
}

interface BulkActionsBarProps {
  selectionMode: 'visible' | 'all';
  selectedIds: string[];
  excludedIds: string[];
  totalCount: number;
  filterContext: FilterContext;
  onClear: () => void;
  tags: { id: string; name: string; color: string | null }[];
  segments: { id: string; name: string }[];
  onDone: () => void;
}

export function BulkActionsBar({
  selectionMode,
  selectedIds,
  excludedIds,
  totalCount,
  filterContext,
  onClear,
  tags,
  segments,
  onDone,
}: BulkActionsBarProps) {
  const { merchant } = useMerchant();
  const [busy, setBusy] = useState(false);

  const selectedCount = selectionMode === 'all'
    ? totalCount - excludedIds.length
    : selectedIds.length;

  // Get all matching customer IDs for "select all" mode
  const getTargetIds = async (): Promise<string[]> => {
    if (selectionMode === 'visible') return selectedIds;

    if (!merchant) return [];

    // Build the same filtered query to get all matching IDs
    let query = epicengageSupabase
      .from('customers')
      .select('id')
      .eq('merchant_id', merchant.id);

    if (filterContext.search) {
      query = query.or(
        `email.ilike.%${filterContext.search}%,first_name.ilike.%${filterContext.search}%,last_name.ilike.%${filterContext.search}%,name.ilike.%${filterContext.search}%,phone.ilike.%${filterContext.search}%`
      );
    }

    if (filterContext.statusFilter !== 'ALL' && filterContext.statusFilter !== 'UNSUBSCRIBED') {
      query = query.eq('status_tag', filterContext.statusFilter);
    }

    // Segment filter
    if (filterContext.selectedSegmentId !== 'ALL') {
      const { data: memberIds } = await epicengageSupabase
        .from('customer_segments')
        .select('customer_id')
        .eq('segment_id', filterContext.selectedSegmentId);
      const ids = (memberIds || []).map((m: any) => m.customer_id);
      if (ids.length === 0) return [];
      query = query.in('id', ids);
    }

    // Tag filter
    if (filterContext.selectedTagIds.length > 0) {
      const { data: taggedIds } = await epicengageSupabase
        .from('customer_tags')
        .select('customer_id')
        .in('tag_id', filterContext.selectedTagIds);
      const ids = (taggedIds || []).map((m: any) => m.customer_id);
      if (ids.length === 0) return [];
      query = query.in('id', ids);
    }

    // Exclude manually unchecked
    if (excludedIds.length > 0) {
      // Supabase doesn't have a NOT IN, so we filter client-side from batched results
    }

    // Fetch all IDs (paginated in batches of 1000)
    const allIds: string[] = [];
    let from = 0;
    const batchSize = 1000;
    while (true) {
      const { data } = await query.range(from, from + batchSize - 1);
      if (!data || data.length === 0) break;
      allIds.push(...data.map((d: any) => d.id));
      if (data.length < batchSize) break;
      from += batchSize;
    }

    // Filter out excluded
    if (excludedIds.length > 0) {
      const excSet = new Set(excludedIds);
      return allIds.filter((id) => !excSet.has(id));
    }

    return allIds;
  };

  const runBulkAction = async (action: (ids: string[]) => Promise<void>) => {
    setBusy(true);
    try {
      const ids = await getTargetIds();
      if (ids.length === 0) {
        toast.error('No customers to operate on');
        return;
      }
      await action(ids);
    } finally {
      setBusy(false);
    }
  };

  const assignTags = (tagId: string) => runBulkAction(async (ids) => {
    // Batch in groups of 500
    for (let i = 0; i < ids.length; i += 500) {
      const batch = ids.slice(i, i + 500);
      const rows = batch.map((cid) => ({ merchant_id: merchant!.id, customer_id: cid, tag_id: tagId }));
      const { error } = await epicengageSupabase.from('customer_tags').upsert(rows, { onConflict: 'customer_id,tag_id' });
      if (error) { toast.error(error.message); return; }
    }
    toast.success(`Tag assigned to ${ids.length} customers`);
    onDone();
  });

  const removeTags = (tagId: string) => runBulkAction(async (ids) => {
    for (let i = 0; i < ids.length; i += 500) {
      const batch = ids.slice(i, i + 500);
      const { error } = await epicengageSupabase.from('customer_tags').delete().eq('tag_id', tagId).in('customer_id', batch);
      if (error) { toast.error(error.message); return; }
    }
    toast.success(`Tag removed from ${ids.length} customers`);
    onDone();
  });

  const assignSegment = (segId: string) => runBulkAction(async (ids) => {
    for (let i = 0; i < ids.length; i += 500) {
      const batch = ids.slice(i, i + 500);
      const rows = batch.map((cid) => ({ merchant_id: merchant!.id, customer_id: cid, segment_id: segId }));
      const { error } = await epicengageSupabase.from('customer_segments').upsert(rows, { onConflict: 'customer_id,segment_id' });
      if (error) { toast.error(error.message); return; }
    }
    toast.success(`Segment assigned to ${ids.length} customers`);
    onDone();
  });

  const removeSegment = (segId: string) => runBulkAction(async (ids) => {
    for (let i = 0; i < ids.length; i += 500) {
      const batch = ids.slice(i, i + 500);
      const { error } = await epicengageSupabase.from('customer_segments').delete().eq('segment_id', segId).in('customer_id', batch);
      if (error) { toast.error(error.message); return; }
    }
    toast.success(`Segment removed from ${ids.length} customers`);
    onDone();
  });

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
      <span className="text-sm font-medium">
        {selectionMode === 'all'
          ? `All ${selectedCount.toLocaleString()} selected`
          : `${selectedCount} selected`}
      </span>

      {/* Assign Tag */}
      <Popover>
        <PopoverTrigger asChild>
          <Button size="sm" variant="outline" disabled={busy}><Tags className="h-3 w-3 mr-1" />Add Tag</Button>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-2">
          {tags.map((t) => (
            <Button key={t.id} size="sm" variant="ghost" className="w-full justify-start" onClick={() => assignTags(t.id)}>
              <span className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: t.color || '#888' }} />
              {t.name}
            </Button>
          ))}
          {tags.length === 0 && <p className="text-xs text-muted-foreground p-2">No tags</p>}
        </PopoverContent>
      </Popover>

      {/* Remove Tag */}
      <Popover>
        <PopoverTrigger asChild>
          <Button size="sm" variant="outline" disabled={busy}><Tags className="h-3 w-3 mr-1" />Remove Tag</Button>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-2">
          {tags.map((t) => (
            <Button key={t.id} size="sm" variant="ghost" className="w-full justify-start" onClick={() => removeTags(t.id)}>
              {t.name}
            </Button>
          ))}
          {tags.length === 0 && <p className="text-xs text-muted-foreground p-2">No tags</p>}
        </PopoverContent>
      </Popover>

      {/* Add to Segment */}
      <Popover>
        <PopoverTrigger asChild>
          <Button size="sm" variant="outline" disabled={busy}><FolderPlus className="h-3 w-3 mr-1" />Add to Segment</Button>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-2">
          {segments.map((s) => (
            <Button key={s.id} size="sm" variant="ghost" className="w-full justify-start" onClick={() => assignSegment(s.id)}>{s.name}</Button>
          ))}
          {segments.length === 0 && <p className="text-xs text-muted-foreground p-2">No segments</p>}
        </PopoverContent>
      </Popover>

      {/* Remove from Segment */}
      <Popover>
        <PopoverTrigger asChild>
          <Button size="sm" variant="outline" disabled={busy}><FolderMinus className="h-3 w-3 mr-1" />Remove from Segment</Button>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-2">
          {segments.map((s) => (
            <Button key={s.id} size="sm" variant="ghost" className="w-full justify-start" onClick={() => removeSegment(s.id)}>{s.name}</Button>
          ))}
          {segments.length === 0 && <p className="text-xs text-muted-foreground p-2">No segments</p>}
        </PopoverContent>
      </Popover>

      <Button size="sm" variant="ghost" onClick={onClear}><X className="h-3 w-3" /></Button>
    </div>
  );
}
